/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { IServerSideGetRowsParams } from 'ag-grid-community';
import { addDays, formatISO, parseISO } from 'date-fns';
import { set } from 'lodash';

const NUMERIC_SET_FILTER_FIELDS = new Set(['pregnant', 'exportDonor']);
const ENUM_SET_FILTER_FIELDS = new Set(['breed.subFamily']);

const TEXT_FILTER_OP_MAP: Record<string, string> = {
  contains: 'contains',
  notContains: 'ncontains',
  equals: 'eq',
  notEqual: 'neq',
  startsWith: 'startsWith',
  endsWith: 'endsWith',
  blank: 'eq',
  notBlank: 'neq',
};

const NUMBER_FILTER_OP_MAP: Record<string, string> = {
  equals: 'eq',
  notEqual: 'neq',
  greaterThan: 'gt',
  greaterThanOrEqual: 'gte',
  lessThan: 'lt',
  lessThanOrEqual: 'lte',
  blank: 'eq',
  notBlank: 'neq',
};

const DATE_FILTER_OP_MAP: Record<string, string> = {
  blank: 'eq',
  notBlank: 'neq',
};

type FilterValue = Record<string, unknown>;

function startOfDay(dateFrom: string) {
  return formatISO(parseISO(dateFrom.slice(0, 10)), {
    representation: 'complete',
  });
}

function startOfNextDay(dateFrom: string) {
  return formatISO(addDays(parseISO(dateFrom.slice(0, 10)), 1), {
    representation: 'complete',
  });
}

function setNestedFilter(field: string, filterValue: FilterValue) {
  const [parent, child] = field.split('.');

  if (parent === 'donor') {
    return { donor: { name: filterValue } };
  }

  if (parent === 'breed') {
    return { breed: { [child ?? 'code']: filterValue } };
  }

  if (parent === 'owner') {
    return { owner: { code: filterValue } };
  }

  if (parent === 'yard') {
    return { yard: { name: filterValue } };
  }

  return child
    ? { [parent]: { [child]: filterValue } }
    : { [field]: filterValue };
}

function buildFilterValues(condition: any): FilterValue[] {
  const isBlankType =
    condition.type === 'blank' || condition.type === 'notBlank';

  if (condition.filterType === 'set') {
    return condition.values?.length ? [{ in: condition.values }] : [];
  }

  if (condition.filterType === 'text') {
    const op = TEXT_FILTER_OP_MAP[condition.type];
    if (!op) return [];

    const value = isBlankType ? null : condition.filter;
    return value == null && !isBlankType ? [] : [{ [op]: value }];
  }

  if (condition.filterType === 'number') {
    if (condition.type === 'inRange') {
      return [
        condition.filter == null ? null : { gte: condition.filter },
        condition.filterTo == null ? null : { lte: condition.filterTo },
      ].filter(Boolean) as FilterValue[];
    }

    const op = NUMBER_FILTER_OP_MAP[condition.type];
    if (!op) return [];

    const value = isBlankType ? null : condition.filter;
    return value == null && !isBlankType ? [] : [{ [op]: value }];
  }

  if (condition.filterType === 'date') {
    const op = DATE_FILTER_OP_MAP[condition.type];
    if (op) return [{ [op]: null }];

    if (!condition.dateFrom && !condition.dateTo) return [];

    switch (condition.type) {
      case 'equals':
        return [
          { gte: startOfDay(condition.dateFrom) },
          { lt: startOfNextDay(condition.dateFrom) },
        ];
      case 'notEqual':
        return [
          {
            or: [
              { lt: startOfDay(condition.dateFrom) },
              { gte: startOfNextDay(condition.dateFrom) },
            ],
          },
        ];
      case 'lessThan':
        return [{ lt: startOfDay(condition.dateFrom) }];
      case 'lessThanOrEqual':
        return [{ lt: startOfNextDay(condition.dateFrom) }];
      case 'greaterThan':
        return [{ gte: startOfNextDay(condition.dateFrom) }];
      case 'greaterThanOrEqual':
        return [{ gte: startOfDay(condition.dateFrom) }];
      case 'inRange':
        return [
          condition.dateFrom ? { gte: startOfDay(condition.dateFrom) } : null,
          condition.dateTo ? { lt: startOfNextDay(condition.dateTo) } : null,
        ].filter(Boolean) as FilterValue[];
      default:
        return [];
    }
  }

  return [];
}

export function buildAdvancedDonorFilterConditions(
  field: string,
  condition: any,
) {
  if (condition.filterType === 'set') {
    const rawValues = Array.isArray(condition.values) ? condition.values : [];
    const values = NUMERIC_SET_FILTER_FIELDS.has(field)
      ? rawValues.map(Number)
      : rawValues;

    if (ENUM_SET_FILTER_FIELDS.has(field)) {
      if (values.length === 0) return [];

      const [parent, child] = field.split('.');
      return [
        {
          or: values.map((value: string) => ({
            [parent]: { [child]: { eq: value } },
          })),
        },
      ];
    }

    condition = { ...condition, values };
  }

  return buildFilterValues(condition).map((filterValue) => {
    if ('or' in filterValue) {
      return {
        or: (filterValue.or as FilterValue[]).map((value) =>
          setNestedFilter(field, value),
        ),
      };
    }

    return setNestedFilter(field, filterValue);
  });
}

interface IDataSourceProps {
  query: any;
  variables?: any;
  skip?: boolean;
  searchBy: string;
  searchTerm: string;
  ownerId?: string;
}

export function useAdvancedDonorDataSource({
  query,
  variables,
  skip,
  searchBy,
  searchTerm,
  ownerId,
}: IDataSourceProps) {
  const apiRef = useRef<{ api: any } | null>(null);

  const getRows = useCallback(
    ({ request, success, fail: _fail, api }: IServerSideGetRowsParams) => {
      if (
        skip ||
        typeof request.startRow === 'undefined' ||
        typeof request.endRow === 'undefined'
      ) {
        success({
          rowData: [],
          rowCount: 0,
        });
        return;
      }

      apiRef.current = { api };

      const order = request.sortModel
        .map((s) => ({
          sort: s.sort,
          field: api.getColumn(s.colId)?.getColDef().field,
        }))
        .filter((s) => s.field)
        .map((s) => set({}, s.field!, s.sort === 'asc' ? 'ASC' : 'DESC'));

      const filterModel = request.filterModel;
      const filterConditions: any[] = [];

      Object.entries(filterModel || {}).forEach(([field, condition]) => {
        filterConditions.push(
          ...buildAdvancedDonorFilterConditions(field, condition),
        );
      });

      const hasLeadingWildcard = searchTerm.startsWith('*');
      const hasTrailingWildcard = searchTerm.endsWith('*');
      const cleanSearchTerm = searchTerm.replace(/^\*+|\*+$/g, '');

      let searchOperator = 'contains';
      if (hasLeadingWildcard && !hasTrailingWildcard) {
        searchOperator = 'endsWith';
      } else if (hasTrailingWildcard && !hasLeadingWildcard) {
        searchOperator = 'startsWith';
      }

      const actualSearchTerm = cleanSearchTerm || searchTerm;

      const ENUM_FIELDS = new Set(['breed.subFamily']);

      const searchCondition =
        searchBy.length === 0 || ENUM_FIELDS.has(searchBy)
          ? {
              or: [
                { name: { [searchOperator]: actualSearchTerm } },
                { breed: { code: { [searchOperator]: actualSearchTerm } } },
                { owner: { code: { [searchOperator]: actualSearchTerm } } },
                { registrationNumber: { [searchOperator]: actualSearchTerm } },
                { tattoo: { [searchOperator]: actualSearchTerm } },
                { metalTag: { [searchOperator]: actualSearchTerm } },
                { yard: { name: { [searchOperator]: actualSearchTerm } } },
                { ranchTag: { [searchOperator]: actualSearchTerm } },
                { lastActivity: { [searchOperator]: actualSearchTerm } },
              ],
            }
          : searchBy.includes('.')
            ? {
                [searchBy.split('.')[0]]: {
                  [searchBy.split('.')[1]]: {
                    [searchOperator]: actualSearchTerm,
                  },
                },
              }
            : { [searchBy]: { [searchOperator]: actualSearchTerm } };

      const where = {
        and: [
          ...filterConditions,
          searchCondition,
          ownerId ? { ownerId: { eq: ownerId } } : null,
        ].filter(Boolean),
      };

      query({
        skip: request.startRow,
        take: request.endRow - request.startRow,
        order,
        where,
        ...variables,
      })
        .unwrap()
        .then((res: any) => {
          success({
            rowData: res.donors.items,
            rowCount: res.donors.totalCount,
          });
        });
    },
    [ownerId, query, searchBy, searchTerm, skip, variables],
  );

  const refetch = useCallback(() => {
    if (apiRef.current?.api) {
      apiRef.current.api.refreshServerSide({ purge: true });
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [searchBy, searchTerm, ownerId, refetch]);

  return useMemo(
    () => ({
      dataSource: { getRows },
      refetch,
    }),
    [getRows, refetch],
  );
}
